import moment from 'moment-timezone';
import { getValue } from '@helpers';

export const objectToQuery = (obj) => {
  let query = '';
  Object.keys(obj).map((key, i) => {
    if (i !== 0) {
      query += '&';
    }
    query += `${key}=${obj[key]}`;
  });
  return query;
};
export const getOffset = (date = '') => {
  // Parse the input date string with moment
  const dateParsed = moment.parseZone(date);

  // Get the timezone offset in the format +HH:mm or -HH:mm
  return dateParsed.format('Z');
};

/**
 * Helper functions to work with new Power Management API format
 * Format: { signals: [{ name, metricType, timeSeriesValues }] }
 */

/**
 * Find a signal by name and optionally metricType
 */
export const findSignal = (signals, name, metricType = null) => {
  if (!signals || !Array.isArray(signals)) return null;
  return signals.find(signal => {
    const nameMatch = signal.name === name;
    return metricType ? nameMatch && signal.metricType === metricType : nameMatch;
  });
};

/**
 * Get all generator signals grouped by generator number and metric type
 * Returns: { '1': { 'Load': {...}, 'SFOC': {...} }, '2': { 'Load': {...}, 'SFOC': {...} } }
 */
export const getGeneratorSignalsMap = (signals) => {
  if (!signals || !Array.isArray(signals)) return {};
  
  const generatorMap = {};
  signals.forEach(signal => {
    if (signal.name && signal.name.startsWith('Generator ')) {
      const generatorNum = signal.name.replace('Generator ', '');
      if (!generatorMap[generatorNum]) {
        generatorMap[generatorNum] = {};
      }
      generatorMap[generatorNum][signal.metricType] = signal.timeSeriesValues || {};
      // Keep null when the backend reports no summary - a missing summary must stay
      // distinguishable from a genuine 0 so consumers can exclude the engine.
      generatorMap[generatorNum][`${signal.metricType}_Summary`] =
        signal.periodSummaryValue ?? null;
    }
  });
  
  return generatorMap;
};

/**
 * Find an overview signal by equipment name and metric type
 */
export const findOverviewSignal = (signals, equipmentName, metricType) => {
  if (!signals || !Array.isArray(signals)) return null;
  return signals.find(signal => 
    signal.name === equipmentName && signal.metricType === metricType
  );
};

/**
 * Find equipment component by name
 */
export const findEquipmentComponent = (componentCalculations, componentName) => {
  if (!componentCalculations || !Array.isArray(componentCalculations)) return null;
  return componentCalculations.find(component => component.name === componentName);
};

/**
 * Find equipment signal by component name and metric type
 */
export const findEquipmentSignal = (componentCalculations, componentName, metricType) => {
  const component = findEquipmentComponent(componentCalculations, componentName);
  if (!component?.signals) return null;
  return component.signals.find(signal => 
    signal.name === componentName && signal.metricType === metricType
  );
};

/**
 * True when a time series carries at least one numeric sample (false for empty or all-null series)
 */
export const hasTimeSeriesValues = (timeSeriesValues) => {
  if (!timeSeriesValues || typeof timeSeriesValues !== 'object') return false;
  return Object.values(timeSeriesValues).some((value) => getValue(value, false) !== null);
};

/**
 * Find drilling activity event by name and metric type
 */
export const findDrillingEvent = (events, name, metricType) => {
  if (!events || !Array.isArray(events)) return null;
  return events.find(event => 
    event.name === name && event.metricType === metricType
  );
};
